import { useEffect, useRef, useState } from "react";
import { auth } from "../lib/firebase";
import { API_BASE_URL, authenticatedFetch } from "../lib/api";
import { ACTIVE_TENANT_HEADER, signInMayNameTenant } from "../lib/activeTenant";
import { billingTenantId } from "../lib/billingAccess";
import { STRIPE_PRICE_IDS } from "../stripe-config";
import { SubscriptionStatus } from "../types";
import { cancelledBeforeStopping, DeletionReceipt, lateRefusalNotice, OwnedOrgWithMembers, parseDeletionReceipt, parseDeletionRefusal } from "../lib/accountDeletion";
import { disconnectGmail, getGmailConnection, GMAIL_LINK_SCANNING_FLAG } from "../lib/gmailApi";
import {
  describeGmailDeletionBlock,
  describeGmailDisconnectedAnyway,
  disconnectGmailBeforeDeletion,
  GMAIL_GATE_TIMEOUT_MS,
} from "../lib/gmailDeletionGate";
// ENG-2576: these two were defined and exported with zero callers. They are wired HERE, into the
// handlers they describe, rather than deleted — the Stripe endpoints behind them work, and the only
// reason they cannot fire is that no UI reaches these handlers today (the purchase/cancel flows went
// with the free-for-all pivot). Wiring them means reviving a flow brings its analytics back with it,
// and turns "never called from anywhere" — which reads as an oversight — into the true state.
import { trackSubscriptionCancelled, trackSubscriptionUpgrade } from "../lib/analytics";

/** An error carrying [notice] after its own message, or the error unchanged when there is none. */
function withNotice(err: unknown, notice: string | undefined): unknown {
  if (!notice) return err;
  const message = err instanceof Error ? err.message : "Failed to delete account";
  return new Error(`${message.replace(/[.\s]*$/, "")}. ${notice}`);
}

/** Validate that a URL points to a known Stripe domain before redirecting. */
function isValidStripeRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && (
      parsed.hostname === 'checkout.stripe.com' ||
      parsed.hostname === 'billing.stripe.com'
    );
  } catch {
    return false;
  }
}

export function useSubscription() {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  // ENG-2534: set ONLY on a successful read, never in `finally`. The gate reads null as "not an
  // admin", which is the same answer it gives a genuine free user — so without this a failed fetch
  // silently downgraded a business admin's console. Matches the *Loaded convention the org-admin
  // hooks already use (ENG-2591/2595): a failed load leaves the flag false.
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  /**
   * ENG-2857: the last /subscription-status attempt terminated without an answer.
   *
   * `subscriptionLoaded` flips true only on success, which is what makes the ENG-2534 gate
   * fail-safe — but it also means a terminal failure leaves the nav saying "Checking your
   * organization…" forever, with no error and no way out but a reload. That is the acceptance
   * criterion ENG-2534 did not meet. This is the signal that lets the nav stop waiting.
   */
  const [subscriptionFailed, setSubscriptionFailed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [loadingBillingPortal, setLoadingBillingPortal] = useState(false);
  const [showPricingCards, setShowPricingCards] = useState(
    window.location.hash.replace('#', '') === 'subscribe'
  );

  // Reset loading states when page is restored from bfcache (browser back button)
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setLoadingBillingPortal(false);
        setIsSubscribing(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // ENG-2963: collapses concurrent calls into the ONE request already in flight. Confirmed live
  // (Performance API timings) that /subscription-status fires twice back to back on Overview's
  // initial load, before any /business/* data — Firebase's onAuthStateChanged can itself notify
  // twice for a single sign-in (an immediate callback for the cached/persisted user, then again
  // once it finishes revalidating), and this is the one place `fetchSubscriptionStatus` is called
  // unconditionally on that path (App.tsx's auth listener). Rather than chase which caller fires
  // twice, every caller now shares one in-flight request — the second gets the first's result
  // instead of issuing its own round-trip. Cleared once the request settles so a later, genuinely
  // fresh call (after a mutation, a retry, etc.) still hits the network.
  const inFlight = useRef<Promise<SubscriptionStatus | null> | null>(null);
  /**
   * ENG-3406: bumped by [reset] at sign-out. A request started for the previous account answers with
   * that account's plan, so once the generation has moved on it sets nothing and resolves null, and it
   * is no longer the shared in-flight request: the next account's first call starts its own, rather
   * than being handed the previous account's answer by the ENG-2963 de-duplication above.
   */
  const generation = useRef(0);

  const fetchSubscriptionStatus = (): Promise<SubscriptionStatus | null> => {
    if (inFlight.current) return inFlight.current;
    if (!auth.currentUser) return Promise.resolve(null);

    const gen = generation.current;
    const current = () => gen === generation.current;
    const promise = (async () => {
      setLoadingSubscription(true);
      // Cleared up front so a successful retry clears the error rather than leaving it on screen.
      setSubscriptionFailed(false);
      try {
        const response = await authenticatedFetch('/subscription-status');
        if (!current()) return null;

        if (response.ok) {
          const data = await response.json();
          if (!current()) return null;
          setSubscriptionStatus(data);
          setSubscriptionLoaded(true);
          return data;
        } else {
          // Deliberately NOT setSubscriptionLoaded(true): the gate must still not resolve, or a failed
          // fetch would derive isTenantAdmin=false and hide the console — the original ENG-2534 bug.
          // This only records that we stopped trying, so the UI can say so.
          setSubscriptionFailed(true);
          return null;
        }
      } catch {
        if (current()) setSubscriptionFailed(true);
        return null;
      } finally {
        if (current()) {
          setLoadingSubscription(false);
          inFlight.current = null;
        }
      }
    })();
    inFlight.current = promise;
    return promise;
  };

  /**
   * ENG-3406: forget the account that signed out. `subscriptionLoaded` and `subscriptionFailed` were
   * never reset, so the next person's nav showed neither "Checking…" nor "Couldn't load… Try again"
   * while their own status loaded (ENG-2534/ENG-2857) — it showed the previous account's answer.
   */
  const reset = () => {
    generation.current += 1;
    inFlight.current = null;
    setSubscriptionStatus(null);
    setSubscriptionLoaded(false);
    setSubscriptionFailed(false);
    setLoadingSubscription(false);
  };

  const handleUpgradeToPremium = async (customerEmail: string, isYearly = false): Promise<void> => {
    setIsSubscribing(true);

    try {
      const response = await authenticatedFetch('/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({
          priceId: isYearly ? STRIPE_PRICE_IDS.PREMIUM_ANNUAL : STRIPE_PRICE_IDS.PREMIUM_MONTHLY,
          customerEmail,
          successUrl: window.location.origin + '?payment=success',
          cancelUrl: window.location.origin + '?payment=cancelled',
        }),
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const { sessionUrl } = await response.json();
      if (!isValidStripeRedirect(sessionUrl)) throw new Error('Invalid checkout URL');
      // Fired on INTENT — we are handing the browser to Stripe and will not run again either way.
      // The completed purchase is a separate event (trackPurchase, on the ?payment=success return),
      // so an abandoned checkout shows up as an upgrade started with no purchase behind it.
      trackSubscriptionUpgrade((subscriptionStatus?.plan as 'free' | 'premium') || 'free', 'premium');
      window.location.href = sessionUrl;
    } catch (err: unknown) {
      throw err;
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleRecommendationUpgrade = async (customerEmail: string, isYearly = false): Promise<void> => {
    setIsSubscribing(true);

    try {
      const response = await authenticatedFetch('/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({
          priceId: isYearly ? STRIPE_PRICE_IDS.PREMIUM_ANNUAL : STRIPE_PRICE_IDS.PREMIUM_MONTHLY,
          customerEmail,
          successUrl: window.location.origin + '?payment=success&onboarding=true',
          cancelUrl: window.location.origin + '?payment=cancelled&onboarding=true',
        }),
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const { sessionUrl } = await response.json();
      if (!isValidStripeRedirect(sessionUrl)) throw new Error('Invalid checkout URL');
      window.location.href = sessionUrl;
    } catch (err: unknown) {
      throw err;
    } finally {
      setIsSubscribing(false);
    }
  };

  /**
   * ENG-3421: the organization the page shows, named on every billing call (see billingTenantId).
   *
   * [org] is that organization's /subscription-status, passed by the page that shows it. It is not
   * always this hook's own. The org pages show the resolved active organization (ENG-3372, lib/
   * activeOrg.ts), and for an admin of several who never used the switcher that is not the one this
   * hook's /subscription-status describes. Naming this hook's own there opened another
   * organization's Stripe portal from Members & Seats, with no error. Omitted, it is this hook's own,
   * which is what the admin-gate notices describe.
   *
   * A tenant chosen in the switcher is the org pages' tenant too, and `authenticatedFetch` sends it
   * either way.
   */
  const billingHeaders = async (org: SubscriptionStatus | null): Promise<Record<string, string>> => {
    const token = await auth.currentUser?.getIdTokenResult();
    const tenantId = billingTenantId(org, !!token && signInMayNameTenant(token));
    return tenantId ? { [ACTIVE_TENANT_HEADER]: tenantId } : {};
  };

  const handleAddSeats = async (
    additionalSeats: number,
    org: SubscriptionStatus | null = subscriptionStatus,
  ): Promise<{ success: boolean; totalSeats?: number; error?: string }> => {
    try {
      const response = await authenticatedFetch('/tenant/add-seats', {
        method: 'POST',
        headers: await billingHeaders(org),
        body: JSON.stringify({ additionalSeats }),
      });
      const data = await response.json();
      if (response.ok) {
        return { success: true, totalSeats: data.totalSeats };
      } else {
        return { success: false, error: data.error || 'Failed to add seats' };
      }
    } catch (err: unknown) {
      // The backend may have succeeded (Stripe + DB updated) but failed to
      // serialize the response. Refresh subscription status to check actual state.
      const refreshed = await fetchSubscriptionStatus();
      if (refreshed) {
        // Bug #19: Compare delta instead of expected total to avoid stale closure
        if ((refreshed.totalSeats ?? 0) > (subscriptionStatus?.totalSeats || 0)) {
          return { success: true, totalSeats: refreshed.totalSeats };
        }
      }
      return { success: false, error: err instanceof Error ? err.message : 'Failed to add seats' };
    }
  };

  /**
   * [org]: the organization whose billing the clicked button offers (see billingHeaders).
   * [returnPage]: where Stripe sends the person back to. ENG-3479: an organization's billing is on
   * its own Billing page, so that page returns there; a personal subscriber's is still on Settings.
   */
  const handleOpenBillingPortal = async (
    org: SubscriptionStatus | null = subscriptionStatus,
    returnPage: 'settings' | 'billing' = 'settings',
  ): Promise<void> => {
    setLoadingBillingPortal(true);
    try {
      const response = await authenticatedFetch('/create-billing-portal-session', {
        method: 'POST',
        headers: await billingHeaders(org),
        body: JSON.stringify({
          returnUrl: `${window.location.origin}#${returnPage}`,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to open billing portal');
      }

      const data = await response.json();
      if (data.url) {
        if (!isValidStripeRedirect(data.url)) throw new Error('Invalid billing portal URL');
        window.location.href = data.url;
      } else {
        throw new Error('No billing portal URL returned from server');
      }
    } catch (err: unknown) {
      setLoadingBillingPortal(false);
      throw err;
    }
  };

  const handleCancelSubscription = async (): Promise<{ success: boolean; message?: string; error?: string }> => {
    if (!auth.currentUser) return { success: false, error: "Not authenticated" };

    try {
      const response = await authenticatedFetch('/cancel-subscription', {
        method: 'POST',
      });

      const data = await response.json();
      if (response.ok) {
        // Reported from the PRE-cancellation status: after the refetch below the plan is already
        // gone, so reading it then would attribute every cancellation to "free".
        trackSubscriptionCancelled((subscriptionStatus?.plan as 'free' | 'premium') || 'free');
        await fetchSubscriptionStatus();
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error || 'Failed to cancel subscription' };
      }
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to cancel subscription' };
    }
  };

  /**
   * ENG-3341: resolves `{ success: false, blockedBy }` when the server refused because the caller
   * owns an organization other people still belong to. Nothing was deleted, so the Firebase user is
   * left alone and the person stays signed in. Every other failure still throws.
   *
   * [onDeleted] runs once the server HAS deleted the account and before the Firebase user goes, while
   * a token still exists — which is where the exit survey belongs. It used to be sent before the
   * request, so a refused deletion would have recorded someone leaving who had not left.
   *
   * ENG-3404: Gmail is disconnected first, and a deletion that would leave it connected throws
   * before anything is deleted. See gmailDeletionGate.ts for how that is decided. If Gmail was
   * disconnected and the deletion then does not happen, [gmailNotice] (on a refusal) or the thrown
   * message says so.
   *
   * ENG-3369/ENG-3405: the server can also stop part-way without deleting anything: a Stripe cancel
   * that failed (502) or a Firebase user it could not delete (503). Those throw with the server's
   * sentence, which says the account was NOT deleted and names any subscription already cancelled.
   * On success the server has deleted the Firebase user itself, so this only signs out here.
   */
  // ENG-3369: a stopped deletion that had already cancelled a subscription, carried to the retry.
  const cancelledBeforeRetry = useRef(false);

  const handleDeleteAccount = async (onDeleted?: () => Promise<void>): Promise<{
    success: boolean;
    receipt?: DeletionReceipt;
    blockedBy?: OwnedOrgWithMembers[];
    gmailNotice?: string;
    /** ENG-3493: the server refused after it had already signed the person out; its sentence says so. */
    refusalNotice?: string;
  }> => {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false };

    const emailScanningEnabled = subscriptionStatus?.features?.[GMAIL_LINK_SCANNING_FLAG] === true;
    const gmail = await disconnectGmailBeforeDeletion(
      {
        getConnection: () => getGmailConnection({ timeoutMs: GMAIL_GATE_TIMEOUT_MS }),
        disconnect: () => disconnectGmail({ timeoutMs: GMAIL_GATE_TIMEOUT_MS }),
      },
      { emailScanningEnabled, apiBaseUrl: API_BASE_URL },
    );
    if (!gmail.proceed) throw new Error(describeGmailDeletionBlock(gmail, emailScanningEnabled));
    if (gmail.outcome === "unverified") {
      console.warn("Deleting without confirming Gmail is disconnected: the Gmail service did not answer.");
    }
    const gmailNotice = gmail.outcome === "disconnected" ? describeGmailDisconnectedAnyway(emailScanningEnabled) : undefined;

    try {
      let response: Response;
      try {
        response = await authenticatedFetch('/account', {
          method: 'DELETE',
        });
      } catch (err: unknown) {
        throw withNotice(err, gmailNotice);
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (cancelledBeforeStopping(data)) cancelledBeforeRetry.current = true;
        const blockedBy = response.status === 409 ? parseDeletionRefusal(data) : null;
        if (blockedBy) {
          const refusalNotice = lateRefusalNotice(data);
          return {
            success: false,
            blockedBy,
            ...(gmailNotice ? { gmailNotice } : {}),
            ...(refusalNotice ? { refusalNotice } : {}),
          };
        }
        throw withNotice(new Error(data.error || 'Failed to delete account'), gmailNotice);
      }

      const parsed = parseDeletionReceipt(await response.json());
      const receipt = cancelledBeforeRetry.current ? { ...parsed, subscriptionCancelled: true } : parsed;

      // Best-effort: the account is already gone, so nothing here may stop the sign-out below. The ID
      // token this browser holds still verifies after the server deleted the Firebase user, so the
      // survey can still be sent (inferred from Firebase's documented token behaviour: a deleted user's
      // unexpired ID token verifies unless the server checks revocation, which ours does not).
      if (onDeleted) {
        try { await onDeleted(); } catch (e) { console.warn('Post-deletion step failed:', e); }
      }

      if (receipt.signedOutEverywhere) {
        // ENG-3405: the server deleted the Firebase user. `currentUser.delete()` would only fail against
        // a user that no longer exists, so just drop this browser's session.
        await auth.signOut();
      } else {
        // A backend that predates ENG-3405 leaves the Firebase user in place; try to delete it here.
        try {
          await currentUser.delete();
        } catch (e) {
          console.warn('Could not delete Firebase user (may need re-auth):', e);
          await auth.signOut();
        }
      }

      return { success: true, receipt };
    } catch (err: unknown) {
      throw err;
    }
  };

  const submitExitSurvey = async (reasons: string[], otherText: string): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    try {
      await authenticatedFetch('/surveys/submit', {
        method: 'POST',
        body: JSON.stringify({
          accountId: currentUser.uid,
          email: currentUser.email,
          surveyType: 'exit',
          responses: { reasons, otherText },
        }),
      });
    } catch (e) {
      console.warn('Failed to submit exit survey:', e);
    }
  };

  return {
    subscriptionStatus,
    setSubscriptionStatus,
    loadingSubscription,
    subscriptionLoaded,
    subscriptionFailed,
    isSubscribing,
    setIsSubscribing,
    loadingBillingPortal,
    showPricingCards,
    setShowPricingCards,
    fetchSubscriptionStatus,
    reset,
    handleUpgradeToPremium,
    handleRecommendationUpgrade,
    handleAddSeats,
    handleOpenBillingPortal,
    handleCancelSubscription,
    handleDeleteAccount,
    submitExitSurvey,
  };
}
